import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { updateScoreCard } from '../../../api/score-cards/update-score-card'
import { getStaffApplicationCurrentScoreCard } from '../../../api/staff-application/get-staff-application-current-score-card'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import {
  EvalutationEnum,
  evalutationEnumMapping
} from '../../../lib/types/enums/staff-application/evalutation.enum'
import {
  PriorityEnum,
  priorityEnumMapping
} from '../../../lib/types/enums/staff-application/priority.enum'
import {
  StaffApplicationLanguageEnum,
  staffApplicationLanguageEnumMapping
} from '../../../lib/types/enums/staff-application/staff-application-language.enum'
import {
  StaffApplicationStatusEnum,
  staffApplicationStatusEnumMapping
} from '../../../lib/types/enums/staff-application/staff-application-status.enum'
import {
  StaffApplicationType,
  staffApplicationTypeMapping
} from '../../../lib/types/enums/staff-application/staff-application-type.enum'
import { pcrResult } from '../../../lib/types/models/score-card/score-card'
import { StaffApplicationInput } from '../../../lib/types/models/staff-application/staff-application-input'
import { getSelectedEnumOption, showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import { PcrScoreCardModal } from '../pcr/pcr-score-card-form/pcr-score-card-form'
import { mapScoreCardToInput } from './utils'

type Props = {
  staffApplicationId?: number
}

const StaffApplicationFormStatus = ({ staffApplicationId }: Props) => {
  const form = useFormContext<StaffApplicationInput>()
  const { modalState, toggleModal, closeModel } = useModalManager(['scoreCard'])

  const { data, refetch } = useQuery({
    queryKey: ['current-score-card', staffApplicationId],
    queryFn: () => {
      if (staffApplicationId) return getStaffApplicationCurrentScoreCard(staffApplicationId)
    },
    enabled: !!staffApplicationId
  })

  const { mutate, isPending } = useMutation({
    mutationFn: updateScoreCard,
    onSuccess: () => {
      refetch()
      closeModel('scoreCard')
    },
    onError: showAxiosError
  })

  return (
    <InnerBox className="bg-primary-light border-0 " title="Statut de la candidature">
      {data?.pcr && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-4 items-center w-fit">
            <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
              Assigné au PCR
            </Typography>
            <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
              {toFullName(data.pcr.firstName, data.pcr.lastName) || '-'}
            </Typography>
            <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
              Recommandation
            </Typography>

            <Badge
              variant={pcrResult(data?.isApplicantRecommendedForHire).variant}
              text={pcrResult(data?.isApplicantRecommendedForHire).label}
            />

            <Typography variant="label-medium" color={tailwindColors.grey.DEFAULT}>
              Voir l'évaluation
            </Typography>
            <div onClick={() => toggleModal('scoreCard')}>
              <Typography
                variant="label-medium"
                className="text-primary underline text-xs font-normal cursor-pointer"
              >
                Evaluation du PCR
              </Typography>
            </div>
          </div>
        </>
      )}
      <div className="flex gap-3 ">
        <div className="w-full flex flex-col gap-4">
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Statut"
                    labelClassName="bg-primary-light"
                    options={Object.values(StaffApplicationStatusEnum).map((status) => ({
                      label: staffApplicationStatusEnumMapping[status].label,
                      value: status
                    }))}
                    ignoredOptions={[
                      StaffApplicationStatusEnum.ACCEPTED,
                      StaffApplicationStatusEnum.REJECTED_AFTER_INTERVIEW,
                      StaffApplicationStatusEnum.REJECTED_BEFORE_INTERVIEW
                    ]}
                    selectedOption={getSelectedEnumOption(
                      field.value,
                      staffApplicationStatusEnumMapping
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="automaticEvaluation"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Pré-évaluation"
                    labelClassName="bg-primary-light"
                    options={Object.values(EvalutationEnum).map((status) => ({
                      label: evalutationEnumMapping[status].label,
                      value: status
                    }))}
                    selectedOption={getSelectedEnumOption(field.value, evalutationEnumMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="w-full flex flex-col gap-4">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Type de poste"
                    labelClassName="bg-primary-light"
                    options={Object.values(StaffApplicationType).map((status) => ({
                      label: staffApplicationTypeMapping[status].label,
                      value: status
                    }))}
                    selectedOption={getSelectedEnumOption(
                      field.value,
                      staffApplicationTypeMapping
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    label="Priorité"
                    labelClassName="bg-primary-light"
                    options={Object.values(PriorityEnum).map((status) => ({
                      label: priorityEnumMapping[status].label,
                      value: status
                    }))}
                    selectedOption={getSelectedEnumOption(field.value, priorityEnumMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
      <FormField
        control={form.control}
        name="language"
        render={({ field }) => (
          <FormItem className="mt-4 w-full">
            <FormControl>
              <ControlledSelectInput
                {...field}
                label="Langue de la candidature"
                labelClassName="bg-primary-light"
                options={Object.values(StaffApplicationLanguageEnum).map((language) => ({
                  label: staffApplicationLanguageEnumMapping[language].label,
                  value: language
                }))}
                selectedOption={getSelectedEnumOption(
                  field.value,
                  staffApplicationLanguageEnumMapping
                )}
              />
            </FormControl>
          </FormItem>
        )}
      />
      {data && (
        <PcrScoreCardModal
          readOnly
          defaultTab={2}
          status={data.application.status}
          isOpen={modalState.scoreCard}
          onCancel={() => closeModel('scoreCard')}
          staffApplication={data.application}
          defaultValues={mapScoreCardToInput(data)}
          handleSubmit={(values) => mutate({ id: data.id, ...values })}
          isLoading={isPending}
          handleReset={() => closeModel('scoreCard')}
          hasViewButton={false}
        />
      )}
    </InnerBox>
  )
}

export default StaffApplicationFormStatus
